<template>
  <div class="tile-wrapper" ref="tileWrapper">
    <Tile>
      <TileContent
        :title="title"
        :tooltip="tooltip"
        @iconClick="$emit('iconClick')"
      >
        <template slot="chart">
          <KpiCardLoader v-if="loading" />
            <ChartOverlay v-else :message="errorMessage">
              <PieChartBlock
                :data="metricValues"
                :transitionSpeed="chartTransitionSpeed"
                :hasTooltip="hasChartData"
                :radius="chartProps.radius"
                :height="chartProps.height"
                :width="chartProps.width"
                :wrapperWidth="wrapperWidth"
              />
            </ChartOverlay>
        </template>
      </TileContent>
    </Tile>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import KpiCardLoader from './KpiCard/KpiCardLoader.vue';
import {
  getDisplayValue,
} from '../../../services/dashboard/displayFormatService';
import {
  getReportFieldFromSnake,
} from '../../../services/dashboard/reportFieldService';
import Tile from './Tile.vue';
import TileContent from './TileContent.vue';
import ChartOverlay from './ChartOverlay';
import PieChartBlock from './Charting/Shared/Charts/PieChartBlock.vue';

export default {
  props: {
    title: {
      type: String,
      required: true,
    },
    chartProps: {
      type: Object,
      default: () => {},
    },
    tooltip: String,
    chartData: Object,
    loading: Boolean,
    error: Boolean,
  },
  data() {
    return {
      colors: {
        primary: '#4369F4',
        secondary: '#233786',
        tertiary: '#00A6A4',
        blank: '#D3D3D3',
      },
      wrapperWidth: 400,
    };
  },
  computed: {
    ...mapState({
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    metricReportField() {
      return getReportFieldFromSnake(this.allReportFields, this.chartData.measure);
    },
    hasChartData() {
      return !!(this.chartData && this.chartData.rows.length);
    },
    metric() {
      const defaultMetricValues = {
        metricValue: 0,
        unclassifiedMetricValue: 0,
        metricTotal: 0,
      };

      if (!this.hasChartData) return defaultMetricValues;

      const { rows, measure } = this.chartData;

      const row = rows[0] || {};
      const key = `${measure}_for_period`;
      const unclassifiedKey = `unclassified_${measure}_for_period`;

      return {
        metricValue: row[key] || 0,
        unclassifiedMetricValue: row[unclassifiedKey] || 0,
        metricTotal: this.chartData.total || 0,
      };
    },
    metricValues() {
      const { metricValue, unclassifiedMetricValue, metricTotal } = this.metric;

      if (metricValue === 0 && metricTotal === 0) {
        return [{ value: 1, color: this.colors.blank }];
      }

      const nonBrandMetricValue = metricTotal - metricValue - unclassifiedMetricValue;

      return [
        {
          value: nonBrandMetricValue,
          displayValue: getDisplayValue(this.metricReportField, nonBrandMetricValue, 0),
          color: this.colors.secondary,
          metric: this.metricReportField,
          label: 'Non-Brand',
        },
        {
          value: metricValue,
          displayValue: getDisplayValue(this.metricReportField, metricValue, 0),
          color: this.colors.primary,
          metric: this.metricReportField,
          label: 'Brand',
        },
        {
          value: unclassifiedMetricValue,
          displayValue: getDisplayValue(this.metricReportField, unclassifiedMetricValue, 0),
          color: this.colors.tertiary,
          metric: this.metricReportField,
          label: 'Unclassified',
        },
      ];
    },
    chartTransitionSpeed() {
      return this.hasChartData ? 1000 : 0;
    },
    errorMessage() {
      if (this.loading) return '';
      if (this.error || !this.hasChartData) return 'No Data';

      return '';
    },
  },
  methods: {
    handleResize() {
      const wrapperWidth = this.$refs.tileWrapper.offsetWidth;

      this.wrapperWidth = wrapperWidth;
    },
  },
  created() {
    window.addEventListener('resize', this.handleResize);
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  mounted() {
    this.handleResize();
  },
  components: {
    KpiCardLoader,
    Tile,
    TileContent,
    PieChartBlock,
    ChartOverlay,
  },
};
</script>

<style scoped lang="scss">
  .tile-wrapper {
    height: 100%;
  }
  ::v-deep .tile {
    margin: 0px;
  }
  ::v-deep .main-card-wrapper.primary {
    height: calc(100% - 32px);
  }
</style>
